import { useState } from "react";
import Button from "components/Button";
import styles from "./RuleFormHelpModal.module.scss";

const RuleFormHelpModal = ({ isOpen, onClose }) => {
  const [activeSection, setActiveSection] = useState(null);

  const toggleSection = (sectionId) => {
    setActiveSection(activeSection === sectionId ? null : sectionId);
  };

  if (!isOpen) return null;

  const helpContent = (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.helpModal} onClick={(e) => e.stopPropagation()}>
      <div className={styles.header}>
        <h2>RFQ Notifications Help Guide</h2>
        <button className={styles.closeButton} onClick={onClose}>×</button>
      </div>
      
      <div className={styles.content}>
        {/* Overview Section */}
        <section className={styles.section}>
          <button 
            type="button"
            className={`${styles.sectionHeader} ${activeSection === 'overview' ? styles.active : ''}`}
            onClick={() => toggleSection('overview')}
          >
            📋 What is RFQ Notifications?
          </button>
          {activeSection === 'overview' && (
            <div className={styles.sectionContent}>
              <p>
                RFQ Notifications is an automated system that monitors incoming Request for Quote (RFQ) opportunities 
                and sends email notifications when new RFQs match your specified criteria.
              </p>
              <h3>How it works:</h3>
              <ol>
                <li><strong>Create Rules:</strong> Define specific criteria for the types of RFQs you want to be notified about</li>
                <li><strong>System Monitoring:</strong> The system automatically checks new RFQs against your active rules</li>
                <li><strong>Email Notifications:</strong> When a match is found, you receive an email with the RFQ details</li>
              </ol>
              <h3>Benefits:</h3>
              <ul>
                <li>Never miss relevant freight opportunities</li>
                <li>Automated monitoring saves time</li>
                <li>Customize notifications to your specific needs</li>
              </ul>
            </div>
          )}
        </section>

        {/* Rule Creation Examples Section */}
        <section className={styles.section}>
          <button 
            type="button"
            className={`${styles.sectionHeader} ${activeSection === 'examples' ? styles.active : ''}`}
            onClick={() => toggleSection('examples')}
          >
            🎯 Rule Creation Examples
          </button>
          {activeSection === 'examples' && (
            <div className={styles.sectionContent}>
              <p>
                Below are two main use cases for RFQ notifications with step-by-step instructions 
                for setting up each type of rule.
              </p>

              {/* Use Case 1: Monitoring Specific Brokers */}
              <div className={styles.fieldGroup}>
                <h3>Use Case 1: Monitoring Specific Brokers</h3>
                <p>
                  <strong>Goal:</strong> Receive all RFQs from specific contacts regardless of route.
                  Perfect for working with trusted brokers from whom you want to see all opportunities.
                </p>
                
                <h4>Field Configuration:</h4>
                <div className={styles.example}>
                  <p><strong>Rule Name:</strong> "Monitoring [Broker Names]" (optional - leave empty for auto-generation, e.g., "Monitoring John Smith, ABC Logistics")</p>
                  <p><strong>Rule Status:</strong> Active ✅</p>
                  <p><strong>Notification Email:</strong> your.email@uskoexpedite.com ✅</p>
                  <p><strong>Min Total Miles:</strong> 0</p>
                  <p><strong>Max Total Miles:</strong> 9999</p>
                  <p><strong>RFQ Source:</strong> Sylectus</p>
                  <p><strong>Pick up States:</strong> leave empty (all states)</p>
                  <p><strong>Delivery States:</strong> leave empty (all states)</p>
                  <p><strong>Match Pick up and Delivery States:</strong> doesn't matter</p>
                  <p><strong>Accept All Truck Types:</strong> True ✅ (or select specific equipment if specialized)</p>
                  <p><strong>All Customer Contacts:</strong> False ❌</p>
                  <p><strong>Customer Contacts:</strong> select specific contacts ✅</p>
                  <p><strong>Schedule (Optional):</strong></p>
                  <ul>
                    <li><strong>Option 1:</strong> Leave unscheduled for 24/7 notifications from these brokers</li>
                    <li><strong>Option 2:</strong> Add business hours (Mon-Fri, 9:00-17:00) to avoid after-hours alerts</li>
                    <li><strong>Option 3:</strong> Custom schedule matching your availability</li>
                  </ul>
                </div>
                
                <p><strong>What you will receive:</strong></p>
                <ul>
                  <li>All RFQs from selected brokers/contacts</li>
                  <li>Any routes (TX→CA, NY→FL, etc.)</li>
                  <li>Any distance (from local to long haul)</li>
                  <li>Only from Sylectus source</li>
                  <li><strong>If scheduled:</strong> Only during your specified time windows</li>
                </ul>
                
                <p><strong>Best suited for:</strong> Working with reliable brokers who regularly have good opportunities.</p>
              </div>

              {/* Use Case 2: Monitoring Specific Routes */}
              <div className={styles.fieldGroup}>
                <h3>Use Case 2: Monitoring Specific Routes</h3>
                <p>
                  <strong>Goal:</strong> Receive all RFQs for specific geographic routes regardless of broker.
                  Perfect for specializing in certain directions (e.g., Canada freight).
                </p>
                
                <h4>Example: Monitoring Canada Routes</h4>
                <div className={styles.example}>
                  <p><strong>Rule Name:</strong> "Canada Routes" (optional - leave empty for auto-generation)</p>
                  <p><strong>Rule Status:</strong> Active ✅</p>
                  <p><strong>Notification Email:</strong> your.email@uskoexpedite.com ✅</p>
                  <p><strong>Min Total Miles:</strong> 0</p>
                  <p><strong>Max Total Miles:</strong> 9999</p>
                  <p><strong>RFQ Source:</strong> Sylectus</p>
                  <p><strong>Pick up States:</strong> select Canadian provinces (AB, BC, MB, NB, NL, NS, NT, NU, ON, PE, QC, SK, YT) ✅</p>
                  <p><strong>Delivery States:</strong> select Canadian provinces (AB, BC, MB, NB, NL, NS, NT, NU, ON, PE, QC, SK, YT) ✅</p>
                  <p><strong>Match Pick up and Delivery States:</strong> False ❌ (OR logic - either pickup or delivery)</p>
                  <p><strong>Accept All Truck Types:</strong> True ✅ (standard for route monitoring)</p>
                  <p><strong>All Customer Contacts:</strong> True ✅</p>
                  <p><strong>Customer Contacts:</strong> leave empty</p>
                  <p><strong>Schedule (Optional):</strong></p>
                  <ul>
                    <li><strong>Option 1:</strong> Leave unscheduled for 24/7 Canada route monitoring</li>
                    <li><strong>Option 2:</strong> Business hours to avoid after-hours cross-border notifications</li>
                    <li><strong>Option 3:</strong> Extended hours (7:00-19:00) to cover US/Canada business hours</li>
                  </ul>
                </div>
                
                <p><strong>What you will receive:</strong></p>
                <ul>
                  <li>All RFQs with pickup in Canada (Canada → US)</li>
                  <li>All RFQs with delivery to Canada (US → Canada)</li>
                  <li>Intra-Canada shipments (Canada → Canada)</li>
                  <li>From any brokers/contacts</li>
                  <li>Any distance</li>
                  <li><strong>If scheduled:</strong> Only during your specified time windows</li>
                </ul>
                
                <p><strong>Other route examples:</strong></p>
                <ul>
                  <li><strong>Texas routes:</strong> Pick up = TX, Delivery = TX, Match = False</li>
                  <li><strong>East Coast:</strong> Pick up = NY,NJ,PA,CT, Delivery = any</li>
                  <li><strong>Interstate corridors:</strong> Pick up = CA, Delivery = TX</li>
                </ul>
                
                <p><strong>Best suited for:</strong> Specializing in specific geographic directions or working with freight requiring special permits.</p>
              </div>

              {/* Quick Setup Guide */}
              <div className={styles.fieldGroup}>
                <h3>Quick Setup Guide</h3>
                <h4>For monitoring brokers:</h4>
                <ol>
                  <li>Enter notification email</li>
                  <li>Set rule name (optional - system will auto-generate if empty)</li>
                  <li>Set All Customer Contacts = False</li>
                  <li>Select specific contacts from the list</li>
                  <li>Leave other fields as default</li>
                  <li>Test rule before activation</li>
                  <li><strong>Set schedule (optional):</strong> Check "Enable time-based scheduling for notifications" when editing rule</li>
                </ol>
                
                <h4>For monitoring routes:</h4>
                <ol>
                  <li>Enter notification email</li>
                  <li>Set rule name (optional - system will auto-generate if empty)</li>
                  <li>Select desired states for Pickup and/or Delivery</li>
                  <li>Set Match States = False for OR logic</li>
                  <li>Set All Customer Contacts = True</li>
                  <li>Test rule before activation</li>
                  <li><strong>Set schedule (optional):</strong> Check "Enable time-based scheduling for notifications" when editing rule</li>
                </ol>
              </div>
            </div>
          )}
        </section>

        {/* Basic Information Section */}
        <section className={styles.section}>
          <button 
            type="button"
            className={`${styles.sectionHeader} ${activeSection === 'basic' ? styles.active : ''}`}
            onClick={() => toggleSection('basic')}
          >
            ℹ️ Basic Information Fields
          </button>
          {activeSection === 'basic' && (
            <div className={styles.sectionContent}>
              <div className={styles.fieldGroup}>
                <h3>Rule Name</h3>
                <p>
                  <strong>Optional:</strong> Give your rule a descriptive name. If left empty, the system will 
                  automatically generate a name based on your criteria (e.g., "TX to NY via Sylectus").
                </p>
                <p><strong>Example:</strong> "Texas to Northeast Routes" or "Short Haul Opportunities"</p>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Rule Status (Active/Inactive)</h3>
                <p>
                  <strong>Active:</strong> Rule is monitoring new RFQs and sending notifications<br/>
                  <strong>Inactive:</strong> Rule is saved but not monitoring (useful for testing or temporary pause)
                </p>
                <p><strong>Tip:</strong> Start with inactive status to test your rule before enabling notifications.</p>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Notification Email</h3>
                <p>
                  <strong>Required:</strong> Must be a valid @uskoexpedite.com email address where notifications will be sent.
                </p>
                <p><strong>Format:</strong> yourname@uskoexpedite.com</p>
                <p><strong>Note:</strong> Each rule can only have one notification email address.</p>
              </div>
            </div>
          )}
        </section>

        {/* Geographic Criteria Section */}
        <section className={styles.section}>
          <button 
            type="button"
            className={`${styles.sectionHeader} ${activeSection === 'geographic' ? styles.active : ''}`}
            onClick={() => toggleSection('geographic')}
          >
            🗺️ Geographic Criteria
          </button>
          {activeSection === 'geographic' && (
            <div className={styles.sectionContent}>
              <div className={styles.fieldGroup}>
                <h3>Pickup States</h3>
                <p>
                  Select the states where you want to pick up freight. Leave empty to receive notifications 
                  for RFQs from all states.
                </p>
                <p><strong>Multiple Selection:</strong> You can select multiple states to expand your coverage area.</p>
                <p><strong>Example:</strong> Select TX, OK, and NM for Southwest pickup coverage.</p>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Delivery States</h3>
                <p>
                  Select the states where you want to deliver freight. Leave empty to receive notifications 
                  for RFQs delivering to all states.
                </p>
                <p><strong>Strategy:</strong> Consider your truck routing and preferred delivery areas.</p>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Match Both Pickup and Delivery States</h3>
                <p>
                  <strong>Checked (AND logic):</strong> RFQ must match BOTH your pickup states AND delivery states<br/>
                  <strong>Unchecked (OR logic):</strong> RFQ matches if it has EITHER your pickup states OR delivery states
                </p>
                <div className={styles.example}>
                  <p><strong>Example:</strong></p>
                  <p>Your rule: Pickup = TX, Delivery = CA</p>
                  <p>• <strong>Checked:</strong> Only TX→CA routes will match</p>
                  <p>• <strong>Unchecked:</strong> TX→anywhere OR anywhere→CA will match</p>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Distance and Source Section */}
        <section className={styles.section}>
          <button 
            type="button"
            className={`${styles.sectionHeader} ${activeSection === 'distance' ? styles.active : ''}`}
            onClick={() => toggleSection('distance')}
          >
            📏 Distance and Source Filters
          </button>
          {activeSection === 'distance' && (
            <div className={styles.sectionContent}>
              <div className={styles.fieldGroup}>
                <h3>Minimum Miles</h3>
                <p>
                  Set the minimum distance for RFQs you want to receive. Default is 0 miles (no minimum).
                </p>
                <p><strong>Use Case:</strong> Set to 300 if you only want long-haul opportunities.</p>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Maximum Miles</h3>
                <p>
                  Set the maximum distance for RFQs you want to receive. Default is 9999 miles (no maximum).
                </p>
                <p><strong>Use Case:</strong> Set to 200 for local/regional opportunities only.</p>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>RFQ Source</h3>
                <p>
                  Choose which load boards or sources you want to monitor:
                </p>
                <ul>
                  <li><strong>Sylectus:</strong> Sylectus load board</li>
                  <li><strong>DAT:</strong> DAT Power load board</li>
                  <li><strong>LoadLink:</strong> LoadLink load board</li>
                  <li><strong>Multiple Sources:</strong> You can select multiple sources for broader coverage</li>
                </ul>
                <p><strong>Note:</strong> Available sources are dynamically loaded from your system configuration and displayed with proper names.</p>
              </div>
            </div>
          )}
        </section>

        {/* Truck Types Section */}
        <section className={styles.section}>
          <button 
            type="button"
            className={`${styles.sectionHeader} ${activeSection === 'trucktypes' ? styles.active : ''}`}
            onClick={() => toggleSection('trucktypes')}
          >
            🚛 Truck Types
          </button>
          {activeSection === 'trucktypes' && (
            <div className={styles.sectionContent}>
              <div className={styles.fieldGroup}>
                <h3>Accept All Truck Types</h3>
                <p>
                  <strong>Default:</strong> Checked (enabled)<br/>
                  <strong>When checked:</strong> Receive notifications for RFQs with any truck type (Flatbed, Van, Reefer, etc.)<br/>
                  <strong>When unchecked:</strong> Only receive notifications for specific truck types you select
                </p>
                <p><strong>Recommendation:</strong> Keep checked unless you specialize in specific equipment types.</p>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Allowed Truck Types</h3>
                <p>
                  <strong>Availability:</strong> Only appears when "Accept all truck types" is unchecked<br/>
                  <strong>Data Source:</strong> Dynamically loaded based on your selected RFQ sources<br/>
                  <strong>Requirement:</strong> You must select RFQ sources first before truck types will load
                </p>
                <p><strong>Common Truck Types:</strong></p>
                <ul>
                  <li><strong>Flatbed:</strong> Open trailers for construction materials, machinery</li>
                  <li><strong>Van/Dry Van:</strong> Enclosed trailers for general freight</li>
                  <li><strong>Reefer:</strong> Temperature-controlled trailers for food, pharmaceuticals</li>
                  <li><strong>Step Deck:</strong> Lower deck trailers for taller cargo</li>
                  <li><strong>Lowboy:</strong> Very low trailers for heavy equipment</li>
                </ul>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Truck Types Display</h3>
                <p>
                  In the rules list, truck types are shown in a dedicated column:
                </p>
                <ul>
                  <li><strong>"All truck types":</strong> When accepting all equipment types</li>
                  <li><strong>"Flatbed, Van only":</strong> When specific types are selected</li>
                  <li><strong>"Flatbed, Van +2 more":</strong> When more than 3 types are selected</li>
                  <li><strong>"No truck types":</strong> Configuration error - needs to be fixed</li>
                </ul>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Setup Tips</h3>
                <ul>
                  <li><strong>Select sources first:</strong> Choose RFQ sources before configuring truck types</li>
                  <li><strong>Equipment specialization:</strong> Uncheck "Accept all" if you only handle specific equipment</li>
                  <li><strong>Validation:</strong> At least one truck type must be selected when "Accept all" is unchecked</li>
                  <li><strong>Dynamic loading:</strong> Available truck types change based on selected RFQ sources</li>
                </ul>
              </div>
            </div>
          )}
        </section>

        {/* Customer Contacts Section */}
        <section className={styles.section}>
          <button 
            type="button"
            className={`${styles.sectionHeader} ${activeSection === 'contacts' ? styles.active : ''}`}
            onClick={() => toggleSection('contacts')}
          >
            👥 Customer Contacts
          </button>
          {activeSection === 'contacts' && (
            <div className={styles.sectionContent}>
              <div className={styles.fieldGroup}>
                <h3>All Customer Contacts</h3>
                <p>
                  <strong>Checked:</strong> Monitor RFQs from all customer contacts in the system<br/>
                  <strong>Unchecked:</strong> Monitor only RFQs from specific contacts you select
                </p>
                <p><strong>Recommendation:</strong> Start with specific contacts, then expand to all contacts if needed.</p>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Contact Selection Interface</h3>
                <p>
                  The contact selection panel helps you choose specific customer contacts to monitor:
                </p>
                <ul>
                  <li><strong>Available Contacts (Left Panel):</strong> Search and select from all available contacts</li>
                  <li><strong>Selected Contacts (Right Panel):</strong> View and manage your selected contacts</li>
                  <li><strong>Search:</strong> Type email addresses to find specific contacts quickly</li>
                  <li><strong>Bulk Selection:</strong> Use "Add [number]" button to select multiple contacts at once</li>
                  <li><strong>Recent Activity Filter:</strong> "Only contacts with recent activity" checkbox (ON by default)</li>
                  <li><strong>Source Filtering:</strong> Contacts auto-filter based on selected RFQ sources</li>
                </ul>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Contact Filtering</h3>
                <p>
                  Smart filtering helps you find relevant contacts efficiently:
                </p>
                <ul>
                  <li><strong>Recent Activity Filter:</strong> Defaults to ON, shows only contacts active in last 30 days</li>
                  <li><strong>Source-Based Filtering:</strong> When you select RFQ sources, contacts automatically filter to show only those relevant to selected sources</li>
                  <li><strong>Manual Override:</strong> Uncheck "Only contacts with recent activity" to see all contacts including inactive ones</li>
                </ul>
                <p><strong>Best Practice:</strong> Keep recent activity filter ON to focus on active brokers.</p>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Contact Statistics</h3>
                <p>
                  Each contact shows RFQ activity statistics to help you choose the most active customers:
                </p>
                <ul>
                  <li><strong>7d:</strong> Number of RFQs sent by this contact in the last 7 days</li>
                  <li><strong>30d:</strong> Number of RFQs sent by this contact in the last 30 days</li>
                </ul>
                <p><strong>Strategy:</strong> Focus on contacts with consistent activity for better opportunities.</p>
              </div>
            </div>
          )}
        </section>

        {/* Time Scheduling Section */}
        <section className={styles.section}>
          <button 
            type="button"
            className={`${styles.sectionHeader} ${activeSection === 'scheduling' ? styles.active : ''}`}
            onClick={() => toggleSection('scheduling')}
          >
            ⏰ Time-Based Scheduling
          </button>
          {activeSection === 'scheduling' && (
            <div className={styles.sectionContent}>
              <div className={styles.fieldGroup}>
                <h3>Overview</h3>
                <p>
                  Time-based scheduling allows you to control exactly when you receive RFQ notifications. 
                  Instead of 24/7 notifications, you can set specific days and time windows.
                </p>
                <ul>
                  <li><strong>Prevent spam:</strong> No notifications outside your working hours</li>
                  <li><strong>Flexible scheduling:</strong> Different time windows for each day</li>
                  <li><strong>Timezone aware:</strong> Uses your profile timezone automatically</li>
                  <li><strong>Backward compatible:</strong> Existing rules remain 24/7 until you add schedules</li>
                </ul>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>How It Works</h3>
                <ol>
                  <li><strong>Create your rule:</strong> Set up criteria as usual (contacts, states, etc.)</li>
                  <li><strong>Add schedules (optional):</strong> Check "Enable time-based scheduling for notifications" to set time windows</li>
                  <li><strong>Configure weekly grid:</strong> Set different hours for each day of the week</li>
                  <li><strong>Save and activate:</strong> Notifications only sent during your time windows</li>
                </ol>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Schedule Status Indicators</h3>
                <ul>
                  <li><strong>24/7:</strong> No schedules configured - notifications sent all day</li>
                  <li><strong>5 Days:</strong> Scheduled for 5 days per week</li>
                  <li><strong>Inactive:</strong> Rule is turned off</li>
                </ul>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Time Format and Timezone</h3>
                <ul>
                  <li><strong>Format:</strong> 24-hour time (09:00, 17:00, 23:30)</li>
                  <li><strong>Timezone:</strong> Uses your profile timezone (check Profile → Settings)</li>
                  <li><strong>Default:</strong> Pacific Standard Time (PST) if no timezone set</li>
                  <li><strong>Overnight:</strong> Supported (e.g., 23:00-07:00 shows as "23:00-07:00 (next day)")</li>
                </ul>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Quick Setup Templates</h3>
                <ul>
                  <li><strong>Business Hours:</strong> Monday-Friday, 9:00-17:00</li>
                  <li><strong>Extended Hours:</strong> Monday-Friday, 8:00-18:00</li>
                  <li><strong>Weekend Only:</strong> Saturday-Sunday, 10:00-16:00</li>
                  <li><strong>Night Shift:</strong> Monday-Friday, 22:00-06:00</li>
                </ul>
                <p><strong>Access:</strong> Click "Quick Setup" in the Schedule Manager</p>
              </div>
            </div>
          )}
        </section>

        {/* Schedule Configuration Section */}
        <section className={styles.section}>
          <button 
            type="button"
            className={`${styles.sectionHeader} ${activeSection === 'scheduleconfig' ? styles.active : ''}`}
            onClick={() => toggleSection('scheduleconfig')}
          >
            🛠️ Schedule Configuration Guide
          </button>
          {activeSection === 'scheduleconfig' && (
            <div className={styles.sectionContent}>
              <div className={styles.fieldGroup}>
                <h3>Step 1: Access Schedule Manager</h3>
                <ol>
                  <li>Go to RFQ Notifications main page</li>
                  <li>Find your existing rule and click <strong>"Edit"</strong></li>
                  <li>In the edit form, check <strong>"Enable time-based scheduling for notifications"</strong> checkbox</li>
                  <li>Schedule options appear below the checkbox</li>
                </ol>
                <p><strong>Note:</strong> You can only add schedules to existing rules. Create the rule first.</p>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Step 2: Understanding the Weekly Grid</h3>
                <ul>
                  <li><strong>Days:</strong> Monday through Sunday listed vertically</li>
                  <li><strong>Status:</strong> Each day shows "Not scheduled" or current time window</li>
                  <li><strong>Timezone:</strong> Header shows your timezone (e.g., "Weekly Schedule (PST)")</li>
                  <li><strong>Actions:</strong> Add/Edit/Delete buttons for each day</li>
                </ul>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Step 3: Adding Individual Time Windows</h3>
                <ol>
                  <li>Click <strong>"Add"</strong> next to any unscheduled day</li>
                  <li>Schedule form opens with day pre-selected</li>
                  <li>Enter <strong>Start Time</strong> in 24-hour format (e.g., 09:00)</li>
                  <li>Enter <strong>End Time</strong> in 24-hour format (e.g., 17:00)</li>
                  <li>Click <strong>"Save"</strong> to add the time window</li>
                  <li>Day updates to show time range (e.g., "09:00-17:00")</li>
                </ol>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Step 4: Using Quick Setup Templates</h3>
                <ol>
                  <li>Click <strong>"Quick Setup"</strong> button in Schedule Manager</li>
                  <li>Choose from pre-defined templates:</li>
                  <ul>
                    <li><strong>Business Hours (9-5):</strong> Mon-Fri, 09:00-17:00</li>
                    <li><strong>Extended Hours (8-6):</strong> Mon-Fri, 08:00-18:00</li>
                    <li><strong>Weekend Only:</strong> Sat-Sun, 10:00-16:00</li>
                    <li><strong>Night Shift:</strong> Mon-Fri, 22:00-06:00</li>
                  </ul>
                  <li>Preview shows which days will be added</li>
                  <li>Click <strong>"Apply"</strong> to add all time windows at once</li>
                </ol>
                <p><strong>Note:</strong> Templates only add to days that don't already have schedules.</p>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Step 5: Managing Existing Schedules</h3>
                <h4>Editing Time Windows:</h4>
                <ol>
                  <li>Click <strong>"Edit"</strong> next to any scheduled day</li>
                  <li>Modify start time, end time, or both</li>
                  <li>Click <strong>"Save"</strong> to update</li>
                </ol>
                
                <h4>Deleting Time Windows:</h4>
                <ol>
                  <li>Click <strong>"Delete"</strong> next to any scheduled day</li>
                  <li>Confirm deletion when prompted</li>
                  <li>Day returns to "Not scheduled" status</li>
                </ol>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Step 6: Verify and Save</h3>
                <ol>
                  <li>Review all scheduled days in the grid</li>
                  <li>Check that times are correct for your timezone</li>
                  <li>Click <strong>"Save"</strong> or <strong>"Close"</strong> to apply changes</li>
                  <li>Rule status changes from "24/7" to show number of scheduled days</li>
                  <li>Test the rule to verify schedule effectiveness</li>
                </ol>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Advanced Configuration Tips</h3>
                <ul>
                  <li><strong>Overnight Schedules:</strong> Start time can be later than end time (e.g., 23:00-07:00)</li>
                  <li><strong>Mixed Schedules:</strong> Each day can have different time windows</li>
                  <li><strong>Partial Week:</strong> You don't need to schedule all 7 days</li>
                  <li><strong>24/7 Exceptions:</strong> Leave most days unscheduled, add restrictions only where needed</li>
                  <li><strong>Timezone Changes:</strong> Schedules automatically adjust if you change your profile timezone</li>
                </ul>
              </div>
            </div>
          )}
        </section>

        {/* Schedule Examples Section */}
        <section className={styles.section}>
          <button 
            type="button"
            className={`${styles.sectionHeader} ${activeSection === 'scheduleexamples' ? styles.active : ''}`}
            onClick={() => toggleSection('scheduleexamples')}
          >
            📅 Schedule Examples & Patterns
          </button>
          {activeSection === 'scheduleexamples' && (
            <div className={styles.sectionContent}>
              <div className={styles.fieldGroup}>
                <h3>Standard Business Hours</h3>
                <p><strong>Pattern:</strong> Monday-Friday, 9:00 AM - 5:00 PM</p>
                <p><strong>Use Case:</strong> Standard office hours, avoid after-hours and weekend notifications</p>
                <div className={styles.example}>
                  <p><strong>Setup:</strong></p>
                  <ul>
                    <li>Use Quick Setup → "Business Hours (9-5)"</li>
                    <li>Or manually add each weekday: 09:00 - 17:00</li>
                  </ul>
                  <p><strong>Result:</strong> Notifications only weekdays 9 AM - 5 PM</p>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Extended Business Hours</h3>
                <p><strong>Pattern:</strong> Monday-Friday, 8:00 AM - 6:00 PM</p>
                <p><strong>Use Case:</strong> Longer work day, catch early morning and evening opportunities</p>
                <div className={styles.example}>
                  <p><strong>Setup:</strong></p>
                  <ul>
                    <li>Use Quick Setup → "Extended Hours (8-6)"</li>
                    <li>Each weekday: 08:00 - 18:00</li>
                  </ul>
                  <p><strong>Result:</strong> 10-hour notification window on weekdays</p>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Weekend Operations</h3>
                <p><strong>Pattern:</strong> Saturday-Sunday, 10:00 AM - 4:00 PM</p>
                <p><strong>Use Case:</strong> Weekend-only coverage, supplemental weekend monitoring</p>
                <div className={styles.example}>
                  <p><strong>Setup:</strong></p>
                  <ul>
                    <li>Use Quick Setup → "Weekend Only"</li>
                    <li>Saturday: 10:00 - 16:00</li>
                    <li>Sunday: 10:00 - 16:00</li>
                  </ul>
                  <p><strong>Result:</strong> Only weekend notifications during specified hours</p>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Night Shift Coverage</h3>
                <p><strong>Pattern:</strong> Monday-Friday, 10:00 PM - 6:00 AM (next day)</p>
                <p><strong>Use Case:</strong> Night operations, overseas coordination</p>
                <div className={styles.example}>
                  <p><strong>Setup:</strong></p>
                  <ul>
                    <li>Use Quick Setup → "Night Shift"</li>
                    <li>Each weeknight: 22:00 - 06:00</li>
                    <li>Shows as "22:00-06:00 (next day)" in interface</li>
                  </ul>
                  <p><strong>Result:</strong> Overnight notifications Monday-Friday nights</p>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Mixed Custom Schedule</h3>
                <p><strong>Pattern:</strong> Different hours for different days</p>
                <p><strong>Use Case:</strong> Irregular schedules, part-time operations</p>
                <div className={styles.example}>
                  <p><strong>Example Setup:</strong></p>
                  <ul>
                    <li>Monday: 08:00 - 12:00 (morning only)</li>
                    <li>Wednesday: 09:00 - 17:00 (full day)</li>
                    <li>Friday: 13:00 - 21:00 (afternoon/evening)</li>
                    <li>Saturday: 10:00 - 14:00 (weekend half-day)</li>
                  </ul>
                  <p><strong>Setup Process:</strong> Add each day individually with unique time windows</p>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <h3>24/7 with Exceptions</h3>
                <p><strong>Pattern:</strong> Mostly unlimited, but restricted on specific days</p>
                <p><strong>Use Case:</strong> Mostly around-the-clock with planned downtime</p>
                <div className={styles.example}>
                  <p><strong>Example:</strong></p>
                  <ul>
                    <li>Monday-Friday: No schedules (24/7 notifications)</li>
                    <li>Saturday: 09:00 - 17:00 (limited weekend hours)</li>
                    <li>Sunday: No schedule (24/7 notifications)</li>
                  </ul>
                  <p><strong>Setup:</strong> Only add schedules for days needing time restrictions</p>
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Advanced Scheduling Concepts</h3>
                <h4>Overnight Schedules:</h4>
                <ul>
                  <li><strong>Supported:</strong> Start time can be later than end time</li>
                  <li><strong>Example:</strong> 23:00-07:00 spans from 11 PM to 7 AM next day</li>
                  <li><strong>Display:</strong> Shows as "23:00-07:00 (next day)" in interface</li>
                  <li><strong>Use Case:</strong> Night shift operations, international freight</li>
                </ul>

                <h4>Timezone Considerations:</h4>
                <ul>
                  <li><strong>Profile Timezone:</strong> All schedules use your profile timezone</li>
                  <li><strong>Default:</strong> PST if no timezone set in profile</li>
                  <li><strong>International Rules:</strong> Consider time differences for cross-border freight</li>
                  <li><strong>Updates:</strong> Schedule times adjust automatically if you change timezones</li>
                </ul>

                <h4>Seasonal/Temporary Adjustments:</h4>
                <ul>
                  <li><strong>Vacation Coverage:</strong> Temporarily modify schedules for time off</li>
                  <li><strong>Seasonal Changes:</strong> Adjust for busy/slow periods</li>
                  <li><strong>Quick Removal:</strong> Delete schedules to return to 24/7 operation</li>
                  <li><strong>Rule Deactivation:</strong> Turn off entire rule for longer breaks</li>
                </ul>
              </div>
            </div>
          )}
        </section>

        {/* Testing and Management Section */}
        <section className={styles.section}>
          <button 
            type="button"
            className={`${styles.sectionHeader} ${activeSection === 'testing' ? styles.active : ''}`}
            onClick={() => toggleSection('testing')}
          >
            🧪 Testing and Management
          </button>
          {activeSection === 'testing' && (
            <div className={styles.sectionContent}>
              <div className={styles.fieldGroup}>
                <h3>Rule Testing</h3>
                <p>
                  Before activating your rule, you can test it against historical data to see how many 
                  RFQs would have matched your criteria.
                </p>
                <ul>
                  <li><strong>7-day Test:</strong> Shows matches from the last 7 days</li>
                  <li><strong>30-day Test:</strong> Shows matches from the last 30 days</li>
                  <li><strong>Sample RFQs:</strong> View examples of RFQs that matched your criteria</li>
                </ul>
                <p><strong>Best Practice:</strong> Test your rule first, then adjust criteria if needed before activation.</p>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Rule Management</h3>
                <p>
                  After creating rules, you can manage them from the main RFQ Notifications page:
                </p>
                <ul>
                  <li><strong>Edit:</strong> Modify rule criteria at any time</li>
                  <li><strong>Activate/Deactivate:</strong> Turn rules on or off without deleting them</li>
                  <li><strong>Delete:</strong> Permanently remove rules you no longer need</li>
                  <li><strong>Test:</strong> Test existing rules against new historical data</li>
                </ul>
              </div>
            </div>
          )}
        </section>

        {/* Best Practices Section */}
        <section className={styles.section}>
          <button 
            type="button"
            className={`${styles.sectionHeader} ${activeSection === 'bestpractices' ? styles.active : ''}`}
            onClick={() => toggleSection('bestpractices')}
          >
            💡 Best Practices and Tips
          </button>
          {activeSection === 'bestpractices' && (
            <div className={styles.sectionContent}>
              <div className={styles.fieldGroup}>
                <h3>Creating Effective Rules</h3>
                <ul>
                  <li><strong>Start Specific:</strong> Begin with narrow criteria, then expand if needed</li>
                  <li><strong>Test First:</strong> Always test your rule before activation</li>
                  <li><strong>Monitor Performance:</strong> Review rule effectiveness regularly</li>
                  <li><strong>Use Descriptive Names:</strong> Name rules clearly for easy management</li>
                </ul>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Common Use Cases</h3>
                <ul>
                  <li><strong>Regional Focus:</strong> Monitor specific state-to-state corridors</li>
                  <li><strong>Distance-Based:</strong> Separate rules for local vs. long-haul freight</li>
                  <li><strong>Customer-Specific:</strong> Create rules for your most active customers</li>
                  <li><strong>Source-Specific:</strong> Different rules for different load boards</li>
                </ul>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Troubleshooting</h3>
                <h4>General Issues:</h4>
                <ul>
                  <li><strong>No Notifications:</strong> Check if rule is active and email is correct</li>
                  <li><strong>Too Many Notifications:</strong> Narrow your criteria or use more specific contacts</li>
                  <li><strong>Missing Opportunities:</strong> Expand criteria or check if contacts are active</li>
                  <li><strong>Email Issues:</strong> Ensure email address is valid @uskoexpedite.com domain</li>
                  <li><strong>Display Issues:</strong> Rule criteria now show proper source names and formatted state lists for better clarity</li>
                </ul>

                <h4>Schedule-Related Issues:</h4>
                <ul>
                  <li><strong>No notifications during scheduled hours:</strong>
                    <ul>
                      <li>Verify rule is Active (not just scheduled)</li>
                      <li>Check your profile timezone (Profile → Settings)</li>
                      <li>Confirm schedule times are in 24-hour format</li>
                    </ul>
                  </li>
                  <li><strong>Notifications outside scheduled hours:</strong>
                    <ul>
                      <li>Check if rule shows "X Days" not "24/7"</li>
                      <li>Look for overnight schedules spanning unexpected hours</li>
                      <li>Verify schedule times are correct for your timezone</li>
                    </ul>
                  </li>
                  <li><strong>Schedule not saving:</strong>
                    <ul>
                      <li>Ensure start time ≠ end time</li>
                      <li>Check that day doesn't already have a schedule</li>
                      <li>Try refreshing page and adding schedule again</li>
                    </ul>
                  </li>
                  <li><strong>Timezone confusion:</strong>
                    <ul>
                      <li>All times are in YOUR profile timezone</li>
                      <li>Default is PST if no timezone set</li>
                      <li>Change timezone in Profile → Settings</li>
                    </ul>
                  </li>
                </ul>
              </div>
            </div>
          )}
        </section>

        {/* Quick Reference Section */}
        <section className={styles.section}>
          <button 
            type="button"
            className={`${styles.sectionHeader} ${activeSection === 'quickref' ? styles.active : ''}`}
            onClick={() => toggleSection('quickref')}
          >
            📚 Quick Reference
          </button>
          {activeSection === 'quickref' && (
            <div className={styles.sectionContent}>
              <div className={styles.fieldGroup}>
                <h3>Required Fields</h3>
                <ul>
                  <li>✅ Notification Email (@uskoexpedite.com)</li>
                </ul>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Optional Fields</h3>
                <ul>
                  <li>Rule Name (auto-generated if empty)</li>
                  <li>Pickup States (all states if empty)</li>
                  <li>Delivery States (all states if empty)</li>
                  <li>Miles Range (0-9999 if empty)</li>
                  <li><strong>Truck Types (all truck types if "Accept all" checked)</strong></li>
                  <li>Customer Contacts (all contacts if not specified)</li>
                  <li><strong>Time Schedules (24/7 notifications if no schedules)</strong></li>
                </ul>
              </div>

              <div className={styles.fieldGroup}>
                <h3>Schedule Quick Reference</h3>
                <ul>
                  <li><strong>Access:</strong> "Enable time-based scheduling for notifications" checkbox when editing rules</li>
                  <li><strong>Time Format:</strong> 24-hour format (09:00, 17:00, 23:30)</li>
                  <li><strong>Timezone:</strong> Uses your profile timezone (default: PST)</li>
                  <li><strong>Overnight:</strong> Supported (e.g., 23:00-07:00)</li>
                  <li><strong>Quick Setup:</strong> Business Hours, Extended Hours, Weekend Only, Night Shift</li>
                  <li><strong>Status Display:</strong> 24/7 (no schedules), X Days (scheduled days)</li>
                </ul>
              </div>
              
              <div className={styles.fieldGroup}>
                <h3>Default Values</h3>
                <ul>
                  <li>Status: Active</li>
                  <li>RFQ Source: Sylectus</li>
                  <li>Min Miles: 0</li>
                  <li>Max Miles: 9999</li>
                  <li><strong>Accept All Truck Types: True (checked)</strong></li>
                  <li>Geographic Logic: OR (either pickup or delivery)</li>
                </ul>
              </div>
            </div>
          )}
        </section>
      </div>
      
      <div className={styles.footer}>
        <Button onClick={onClose} variant="primary">
          Close Help Guide
        </Button>
      </div>
    </div>
  </div>
);

  return helpContent;
};

export default RuleFormHelpModal;